import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import * as Shopify from "@/lib/shopify";

const CartContext = createContext(null);

const CART_ID_KEY = "adore_shopify_cart_id";

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState(null);
    const [loading, setLoading] = useState(false);

    // Restore cart from localStorage
    useEffect(() => {
        const id = localStorage.getItem(CART_ID_KEY);
        if (!id) return;
        Shopify.getCart(id)
            .then((c) => {
                if (c && c.lines?.length >= 0) setCart(c);
                else localStorage.removeItem(CART_ID_KEY);
            })
            .catch(() => {
                localStorage.removeItem(CART_ID_KEY);
            });
    }, []);

    const addItem = async (variantId, qty = 1) => {
        if (!variantId) {
            toast.error("Please select an option");
            return;
        }
        setLoading(true);
        try {
            let next;
            if (!cart) {
                next = await Shopify.cartCreate([{ merchandiseId: variantId, quantity: qty }]);
                if (next?.id) localStorage.setItem(CART_ID_KEY, next.id);
            } else {
                next = await Shopify.cartLinesAdd(cart.id, [{ merchandiseId: variantId, quantity: qty }]);
            }
            setCart(next);
            return next;
        } catch (e) {
            toast.error(e.message || "Unable to add to cart");
        } finally {
            setLoading(false);
        }
    };

    const updateQty = async (lineId, qty) => {
        if (!cart) return;
        setLoading(true);
        try {
            const next = await Shopify.cartLinesUpdate(cart.id, [
                { id: lineId, quantity: Math.max(1, qty) },
            ]);
            setCart(next);
        } catch (e) {
            toast.error(e.message || "Update failed");
        } finally {
            setLoading(false);
        }
    };

    const removeItem = async (lineId) => {
        if (!cart) return;
        setLoading(true);
        try {
            const next = await Shopify.cartLinesRemove(cart.id, [lineId]);
            setCart(next);
        } catch (e) {
            toast.error(e.message || "Remove failed");
        } finally {
            setLoading(false);
        }
    };

    const clear = () => {
        localStorage.removeItem(CART_ID_KEY);
        setCart(null);
    };

    // Centralised redirect to Shopify's real hosted checkout URL.
    const redirectToCheckout = async () => {
        setLoading(true);
        try {
            const cartItems = cart?.lines || items || [];
            if (cartItems.length === 0) {
                toast.error("Your bag is empty");
                return;
            }

            let url = cart?.checkoutUrl;
            const isRealCheckout = url && (url.includes("/cart/c/") || url.includes("/checkouts/"));

            // If we only have a local cart or a generic URL, generate a real Shopify cart session
            if (!isRealCheckout || !cart?.id || cart?.id?.startsWith("adore_local")) {
                const lines = cartItems.map((i) => ({
                    merchandiseId: i.variantId,
                    quantity: i.quantity,
                }));
                const fresh = await Shopify.cartCreate(lines);
                if (fresh?.checkoutUrl) {
                    url = fresh.checkoutUrl;
                    setCart(fresh);
                    if (fresh.id) localStorage.setItem(CART_ID_KEY, fresh.id);
                }
            }

            if (!url) {
                toast.error("Preparing checkout — please try again in a moment.");
                return;
            }

            // Ensure destination is the Shopify store domain with forwarding disabled (_fd=0)
            let targetUrl = url;
            try {
                const u = new URL(url);
                if (u.hostname === "adore.lighting" || !u.hostname.endsWith(".myshopify.com")) {
                    u.hostname = "v1hj1w-3k.myshopify.com";
                    u.protocol = "https:";
                }
                if (!u.searchParams.has("_fd")) {
                    u.searchParams.set("_fd", "0");
                }
                targetUrl = u.toString();
            } catch {}

            window.location.href = targetUrl;
        } catch (err) {
            console.error("Cart creation failed during checkout redirect", err);
            toast.error("Could not start checkout. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const items = cart?.lines || [];
    const count = cart?.totalQuantity || 0;
    const total = cart?.subtotal || 0;
    const checkoutUrl = cart?.checkoutUrl;

    return (
        <CartContext.Provider
            value={{
                cart,
                items,
                count,
                total,
                checkoutUrl,
                addItem,
                updateQty,
                removeItem,
                clear,
                redirectToCheckout,
                loading,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
